import type { IPageDetailExtractedData } from '../helpers/pageDetailV2';
import type { EAnalyticMode } from '~/modules/analytics/types';
import type { AnalyticDateFilter } from '~/presentation/ui';
import type { ETimeDimension } from '~/core/gemxql';

import { buildDetailQueryVariables, extractPageDetailData } from '../helpers/pageDetailV2';
import { LINE_CHART_METRIC_KEYS } from '../constants/pageAnalyticV2';
import { getTimeDimensionByDate } from '~/core/gemxql/helpers';
import { useCustomPageAnalyticQueryV2 } from '../api';
import { useMemo } from 'react';

export interface IFetchPageDetailsProps {
  mode: EAnalyticMode;
  currentDates: AnalyticDateFilter;
  previousDates: AnalyticDateFilter;
  isQueryPreviousData?: boolean;
  locationPath?: string;
  shopifyPageId?: string;
  isEnabledFilterByShopifyPageId?: boolean;
}

export interface IUseFetchPageDetailsReturn extends IPageDetailExtractedData {
  interval: ETimeDimension;
  isFetching: boolean;
}

export const useFetchPageDetails = ({
  mode,
  currentDates,
  previousDates,
  isQueryPreviousData,
  locationPath,
  shopifyPageId,
  isEnabledFilterByShopifyPageId,
}: IFetchPageDetailsProps): IUseFetchPageDetailsReturn => {
  const interval = useMemo(
    () => getTimeDimensionByDate(currentDates.startDate, currentDates.endDate),
    [currentDates.startDate, currentDates.endDate],
  );

  const variables = useMemo(
    () =>
      buildDetailQueryVariables({
        mode,
        currentDates,
        previousDates,
        isQueryPreviousData,
        interval,
        locationPath,
        shopifyPageId,
        isEnabledFilterByShopifyPageId,
      }),
    [
      mode,
      currentDates,
      previousDates,
      isQueryPreviousData,
      interval,
      locationPath,
      shopifyPageId,
      isEnabledFilterByShopifyPageId,
    ],
  );

  const { data: response, isFetching } = useCustomPageAnalyticQueryV2(variables, {
    enabled: !!variables.input,
  });

  const { totalsRow, comparisonTotalsRow, pageItems, columnTypes, allChartsDataMap } = useMemo(
    () =>
      extractPageDetailData({
        response,
        interval,
        metricKeys: LINE_CHART_METRIC_KEYS,
        hasPrevious: !!isQueryPreviousData,
      }),
    [response, interval, isQueryPreviousData],
  );

  return {
    totalsRow,
    comparisonTotalsRow,
    columnTypes,
    allChartsDataMap,
    pageItems,
    interval,
    isFetching,
  };
};
